import { z } from "zod";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Issue types that can be detected on staged purchase orders.
 * Add new issue types here when implementing detection in the handler.
 */
export const ORDER_ISSUE_TYPES = [
  "unapproved_discount",
  "unmatched_product",
  "order_approval_required",
  "inactive_product",
] as const;

/**
 * Schema for a single detected issue.
 */
export const orderIssueSchema = z.object({
  type: z.enum(ORDER_ISSUE_TYPES),
  description: z.string(),
  lineItemIndex: z.number().optional(),
  lineItemDescription: z.string().optional(),
});

/**
 * Schema for order summary data (used downstream for notifications).
 */
export const orderSummarySchema = z.object({
  purchaseOrderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  customerNumber: z.string().nullable().optional(),
  orderType: z.string().nullable().optional(),
  shipToLocation: z.string().nullable().optional(),
  orderDate: z.string().nullable().optional(),
  requestDate: z.string().nullable().optional(),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  lineItemCount: z.number(),
  receivedDate: z.string().nullable(),
});

/**
 * Schema for a single order's issue detection result.
 */
export const orderIssueResultSchema = z.object({
  stagedPurchaseOrderId: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  issues: z.array(orderIssueSchema),
  issueTypes: z
    .array(z.enum(ORDER_ISSUE_TYPES))
    .describe("Unique issue types found on this order"),
  orderSummary: orderSummarySchema,
});

/**
 * Detect Order Issues Step
 *
 * Pure issue detection step that checks staged purchase orders for problems
 * such as unapproved discounts, unmapped products, validation errors, etc.
 *
 * This step outputs structured issue data that can be used by downstream steps
 * for routing decisions and notification rendering. It does NOT render emails
 * or make approval decisions - those are separate concerns.
 *
 * Outputs are grouped for convenient routing:
 * - ordersWithNoIssues: Orders ready to submit (no blocking issues)
 * - ordersNeedingApproval: Orders blocked on human approval (discount or order-level)
 * - ordersWithOtherBlockers: Orders blocked on a non-approval issue type
 */
export const detectOrderIssuesStep = defineStepConfig({
  type: "detect_order_issues",
  name: "Detect Order Issues",
  description:
    "Checks staged POs for issues (unapproved discounts, etc.) and outputs grouped results for routing",
  category: "Data Access",
  libraryModule: "OrderManagement",
  deprecated: {
    reason:
      "Superseded by the declarative workflow-native approval flow. Use 'Compute Order Facts' to stamp the facts (list price, mismatch, product group) that an Approval step's `appliesWhen` condition routes on — replacing the APPROVAL_NEEDED bucket + recheck-pricing signal.",
    replacedBy: "compute_order_facts",
  },
  inputSchema: z.object({
    stagedPurchaseOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the staged purchase orders to check"),
  }),

  outputSchema: z.object({
    // Full results for each order
    orderResults: z
      .array(orderIssueResultSchema)
      .describe("Detailed issue results for each order checked"),

    // Grouped IDs for routing convenience
    ordersWithNoIssues: z
      .array(z.string().uuid())
      .describe(
        "Order IDs eligible for auto-submission (no blocking issues). " +
          "May include orders with non-blocking issues like unmatched_product.",
      ),
    ordersNeedingApproval: z
      .array(z.string().uuid())
      .describe(
        "Order IDs blocked on human approval — covers both unapproved " +
          "discounts and order-level approval thresholds. These flavors " +
          "share the same routing target (the next approval watch role) so " +
          "they live in one bucket.",
      ),
    ordersWithOtherBlockers: z
      .array(z.string().uuid())
      .describe(
        "Order IDs blocked on a non-approval issue type — i.e. " +
          "isBlockingIssue is true but requiresApproval is false.",
      ),

    // Context for downstream steps
    organizationId: z
      .string()
      .uuid()
      .nullable()
      .describe("Organization ID (null if no orders checked)"),
    customerId: z
      .string()
      .uuid()
      .nullable()
      .describe("Customer ID from first order (null if no orders checked)"),

    // Passthrough
    totalOrdersChecked: z
      .number()
      .describe("Total number of orders that were checked"),

    // Wait-for-signal support
    resolved: z
      .boolean()
      .describe(
        "True when no pricing issues remain (or waitForSignal is false). Used by Temporal waitForSignal loop.",
      ),

    // Multi-level approval outputs for notification routing. Exactly one of
    // these two sockets carries data per run; the other is null. Splitting
    // by system at the wire level keeps the approval-system discriminator
    // out of the data plane — downstream "resolve approval recipients" wires
    // both sockets in and dispatches based on which one is non-null.
    nextDiscountApprovalLevel: z
      .number()
      .int()
      .positive()
      .nullable()
      .describe(
        "The discount approval level number that needs to act next. " +
          "Set when the next pending action is a discount approval. " +
          "Null when no discount approval is pending, or when the next " +
          "action is an order-level approval.",
      ),
    nextOrderApprovalLevel: z
      .number()
      .int()
      .positive()
      .nullable()
      .describe(
        "The order approval level number that needs to act next. " +
          "Set when the next pending action is an order-level approval. " +
          "Null when no order approval is pending, or when the next " +
          "action is a discount approval.",
      ),
    idempotencyKeyPrefix: z
      .string()
      .nullable()
      .describe(
        "Prefix for email idempotency key to prevent duplicate notifications. " +
          "Format: 'approval:{stagedPOId}:discount:L{n}' for discount approvals, " +
          "'order_approval:{stagedPOId}:order:L{n}' for order-level approvals. " +
          "Null when no pending approvals.",
      ),
  }),

  configSchema: z.object({
    issueTypes: z
      .array(z.enum(ORDER_ISSUE_TYPES))
      .default([
        "unapproved_discount",
        "unmatched_product",
        "order_approval_required",
      ])
      .describe("Which issue types to check for"),
    disabled: z
      .boolean()
      .default(false)
      .describe("If true, skip all checks and output empty results"),
    waitForSignal: z
      .boolean()
      .default(false)
      .describe(
        "If true, step waits for blocking issues (e.g. unapproved discounts, order-level approvals) to be resolved via signal before proceeding",
      ),
  }),

  // Wait-for-signal configuration: when enabled, this step will wait for
  // external signals to re-check pricing until all issues are resolved.
  // Only activates when waitForSignal is true in the step's runtime config.
  waitForSignal: {
    signalName: "recheck_pricing",
    maxIterations: 100,
  },

  emits: [SystemEvent.APPROVAL_NEEDED] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});

export type OrderIssueType = (typeof ORDER_ISSUE_TYPES)[number];
export type OrderIssue = z.infer<typeof orderIssueSchema>;
export type OrderSummary = z.infer<typeof orderSummarySchema>;
export type OrderIssueResult = z.infer<typeof orderIssueResultSchema>;
